import { gl } from '../../Engine';
import { VertexBufferObject } from './VertexBufferObject';

type AttributeSize = 1 | 2 | 3 | 4;

/**
 * AttributeVertexBufferObject is used to transfer and store vertex attribute data.
 * It is commonly used to store attributes such as positions, normals,
 * texture coordinates, colors, or any other per-vertex data that is required for rendering.
 *
 * AttributeVertexBufferObject is a subclass of {@link VertexBufferObject},
 * specializing in VBOs of type {@link gl.ARRAY_BUFFER}. VBOs of this type require additional
 * information on the data that is stored within the VBO.
 * Number of components making up a single value as well as the data type of the stored data is needed.
 * AttributeVertexBufferObject makes it possible to store that information alongside the VBO.
 */
export class AttributeVertexBufferObject extends VertexBufferObject {
    /**
     * The attribute size for each value.
     * Meaning the number of components that make up a single value. Such as 2 for 2D data or 3 for 3D data.
     */
    public readonly attrSize: AttributeSize;

    /**
     * Datatype of the data stored in this {@link VertexBufferObject}.
     * The following data types are available:\
     * Data type                         internal format    value range\
     * {@link gl.BYTE}                          int8        [-128, 127]\
     * {@link gl.SHORT}                         int16       [-32768, 32767]\
     * {@link gl.UNSIGNED_BYTE}                 uint8       [0, 255]\
     * {@link gl.UNSIGNED_SHORT}                uint16      [0,65535]\
     * {@link gl.FLOAT}                         float32     floating point number\
     * {@link gl.HALF_FLOAT}                    float16     floating point number\
     * {@link gl.INT}                           int32       [-2147483648, 2147483647]\
     * {@link gl.UNSIGNED_INT}                  uint32      [0, 4294967295]\
     * {@link gl.INT_2_10_10_10_REV}            int32       [-512, 511]\
     * {@link gl.UNSIGNED_INT_2_10_10_10_REV}   uint32      [0, 1023]
     */
    public readonly dataType: number;

    /**
     * Constructor for {@link AttributeVertexBufferObject}
     * Creates the {@link VertexBufferObject} of type {@link gl.ARRAY_BUFFER}.
     * Defines the attributeSize and dataType of the data that is being stored within this VBO.
     * @param attrSize The attribute size for each value.
     * Meaning the number of components that make up a single value. Such as 2 for 2D data or 3 for 3D data.
     * @param dataType type of the data stored in this {@link VertexBufferObject}.
     * The following data types are available:\
     * Data type                         internal format    value range\
     * {@link gl.BYTE}                          int8        [-128, 127]\
     * {@link gl.SHORT}                         int16       [-32768, 32767]\
     * {@link gl.UNSIGNED_BYTE}                 uint8       [0, 255]\
     * {@link gl.UNSIGNED_SHORT}                uint16      [0,65535]\
     * {@link gl.FLOAT}                         float32     floating point number\
     * {@link gl.HALF_FLOAT}                    float16     floating point number\
     * {@link gl.INT}                           int32       [-2147483648, 2147483647]\
     * {@link gl.UNSIGNED_INT}                  uint32      [0, 4294967295]\
     * {@link gl.INT_2_10_10_10_REV}            int32       [-512, 511]\
     * {@link gl.UNSIGNED_INT_2_10_10_10_REV}   uint32      [0, 1023]
     */
    constructor(attrSize: AttributeSize, dataType: number) {
        super(gl.ARRAY_BUFFER);
        this.attrSize = attrSize;
        this.dataType = dataType;
    }

    /**
     * Method to link this {@link AttributeVertexBufferObject} to attribute within the {@link VertexArrayObject}.
     * This method should be called by the {@link VertexArrayObject} when it wants to link VBO to itself.
     * @param attribute The attribute to which this VertexBufferObject should be linked to
     * @returns True to indicate that the VBO linking was successful.
     */
    public setPointer(attribute: number): boolean {
        gl.vertexAttribPointer(
            attribute,
            this.attrSize,
            this.dataType,
            false,
            0,
            0
        );
        return true;
    }
}
